import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SearchIcon } from "assets/img/inbox/search.svg";
import { Avatar, Button, Input } from "components/elements";
import { Separator } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFriends } from "hooks/useFriends";
import { trpc } from "lib/trpc";
import { FriendRequestStatus } from "@compass/shared/types";
import { ClientUser } from "@compass/shared/types";

export const FindNewFriendModal = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const debounceTimeout = 300;

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, debounceTimeout);
        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    const trpcUtils = trpc.useContext();
    // Pass the (debounced) search term to the server so it can search across
    // every user, not just the first 20 returned by `getAllUser`. Without this
    // the modal silently hid users past the cap (PRTL-1254).
    const { data } = useFriends({ search: debouncedSearchTerm });
    const userData = useCurrentUser();

    const friendRequestSentMutation = trpc.users.friendRequestSent.useMutation();
    const friendRequestDeleteMutation = trpc.users.friendRequestDelete.useMutation();
    const friendRequestMutation = trpc.users.friendRequestResponse.useMutation();
    const declineRequest = trpc.users.getDeclineRequest.useQuery();

    // Get organization display name based on user role
    const getOrganizationDisplay = (user: ClientUser) => {
        const compassRoles = ["COMPASS_SUPERUSER", "COMPASS_USER"];

        if (user.role && compassRoles.includes(user.role)) {
            return "Compass CFO Solutions";
        } else if (user.role === "USER") {
            return user.organizationName;
        } else if (user.role && ["GUEST", "READONLY_USER"].includes(user.role)) {
            return null; // Don't display organization for guests
        }

        return user.organizationName; // fallback
    };

    const handleFriendRequestSentResponse = ({ id }: { id: number }) => {
        // Mirror the active query's input so the optimistic update targets the
        // currently-rendered cache entry. `useFriends({ search })` keys on the
        // search term, so a bare `["users.getAllUser"]` cache key would miss
        // the visible page whenever a search is active.
        const queryInput = debouncedSearchTerm.trim()
            ? { search: debouncedSearchTerm.trim() }
            : undefined;
        const previousData = trpcUtils.users.getAllUser.getData(queryInput);
        trpcUtils.users.getAllUser.setData(queryInput, (old) =>
            old
                ? old.map((user) => (user.id === id ? { ...user, sentFriendRequest: true } : user))
                : old,
        );
        friendRequestSentMutation.mutate(
            { userId: id },
            {
                onError: () => {
                    trpcUtils.users.getAllUser.setData(queryInput, previousData);
                },
                onSuccess: () => {
                    trpcUtils.users.getAllUser.invalidate();
                },
            },
        );
    };

    const handleFriendRequestDelete = ({ id }: { id: number }) => {
        try {
            friendRequestDeleteMutation.mutate(
                { userId: id },
                { onSuccess: () => trpcUtils.users.getAllUser.invalidate() },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleFriendRequestResponse = ({
        id,
        status,
    }: {
        id: number;
        status: FriendRequestStatus;
    }) => {
        try {
            friendRequestMutation.mutate(
                { id: id, status: status },
                {
                    onSuccess: () => {
                        trpcUtils.users.me.invalidate();
                        trpcUtils.users.getAllUser.invalidate();
                        setIsOpen(false);
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleSentRequest = ({ id, status }: { id: number; status: FriendRequestStatus }) => {
        const findData = userData?.receivedFriendRequests.find((item) => item.senderId === id);
        if (findData) {
            handleFriendRequestResponse({ id: findData.id, status });
        }
    };

    const manageButton = ({ mode, id }: { mode: string; id: number }) => {
        switch (mode) {
            case "sent":
                return (
                    <Button
                        variant="secondary"
                        size="smaller"
                        onClick={() => handleFriendRequestDelete({ id })}
                    >
                        Cancel
                    </Button>
                );
            // case "request":
            //     return (
            //         <div className="flex gap-2">
            //             <Button
            //                 size="smaller"
            //                 onClick={() => handleFriendRequestResponse({ id, status: "ACCEPTED" })}
            //                 className="text-sm text-white bg-green-500 hover:bg-green-600 px-4 py-1.5"
            //             >
            //                 Accept
            //             </Button>
            //             <Button
            //                 size="smaller"
            //                 onClick={() => handleSentRequest({ id, status: "DECLINED" })}
            //                 className="text-sm text-white bg-red-500 hover:bg-red-600 px-4 py-1.5"
            //             >
            //                 Decline
            //             </Button>
            //         </div>
            //     )
            default:
                return (
                    <Button
                        variant="primary"
                        size="smaller"
                        onClick={() => handleFriendRequestSentResponse({ id })}
                    >
                        Add Friend
                    </Button>
                );
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content
                    className="
                        fixed left-1/2 top-1/2 z-50
                        flex max-h-[90vh]
                        w-[95%] max-w-md
                        -translate-x-1/2 -translate-y-1/2 flex-col
                        rounded-2xl bg-white
                    "
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 pt-5">
                        <h2 className="font-sans text-[24px] font-bold text-[#424242]">
                            Find new friends
                        </h2>
                        <Dialog.Close asChild>
                            <button className=" hover:text-gray-700">
                                <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                            </button>
                        </Dialog.Close>
                    </div>
                    <Separator className="mt-3 mb-5" />
                    {/* <div className="mx-3 mb-3 mt-2 h-[1px] bg-[#cccccc]" /> */}
                    {/* Search bar */}
                    <div className="px-3">
                        <div className="relative">
                            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Type in a name or organization"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full rounded bg-[#def1fa] py-2 pl-9 pr-3 text-sm placeholder:font-semibold placeholder:text-[#424242] focus:outline-none focus:ring-0"
                            />
                        </div>
                    </div>
                    {/* User list */}
                    <div className="max-h-[400px] space-y-4 overflow-y-auto p-3">
                        {data && data.length > 0 ? (
                            data
                                .filter((item) => !declineRequest.data?.includes(item.id))
                                .filter((item) => !item.recivedFriendRequest)
                                .map((item) => {
                                    const organizationDisplay = getOrganizationDisplay(item);

                                    return (
                                        <div
                                            key={item.id}
                                            className="flex items-center justify-between rounded-md px-3 py-2 shadow-sm hover:bg-gray-50"
                                        >
                                            <div className="">
                                                <Link
                                                    to="/profile/$id"
                                                    target="_blank"
                                                    params={{ id: `${item.id}` }}
                                                    className="flex items-center gap-2 text-inherit no-underline focus:outline-none focus:ring-0"
                                                >
                                                    <Avatar
                                                        photoUrl={item.avatarUrl}
                                                        name={item.name}
                                                        className="h-8 w-8"
                                                    />
                                                    <div className="text-sm font-semibold">
                                                        <div>{item.name}</div>
                                                        {organizationDisplay && (
                                                            <div className="text-xs italic text-gray-600">
                                                                {organizationDisplay}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Link>
                                            </div>
                                            <div>
                                                {manageButton({
                                                    mode: item.recivedFriendRequest
                                                        ? "request"
                                                        : item.sentFriendRequest
                                                          ? "sent"
                                                          : "new",
                                                    id: item.id,
                                                })}
                                            </div>
                                        </div>
                                    );
                                })
                        ) : (
                            <div className="py-8 text-center text-sm text-gray-500">
                                No users found
                            </div>
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
